import { useState, useMemo } from 'react'
import { Mail, Target, TrendingUp, Users, BarChart3, CheckCircle2, AlertTriangle, Clock, Search, Filter, Plus, Eye, Trash2, Edit, Send, MousePointerClick } from 'lucide-react'

export default function PhishingSimPage() {
  const [campaigns, setCampaigns] = useState([
    { id: 'CAM-2025-001', name: 'Q1 Security Awareness Campaign', template: 'Fake Invoice Alert', sendDate: 'Sep 25, 2025', targetGroups: 'All Employees', emailsSent: 2847, clicked: 342, reported: 156, openRate: '48%', clickRate: '12%', reportRate: '5.5%', status: 'Completed', severity: 'High' },
    { id: 'CAM-2025-002', name: 'Finance Department Phishing Test', template: 'CEO Impersonation', sendDate: 'Sep 24, 2025', targetGroups: 'Finance Team', emailsSent: 145, clicked: 23, reported: 89, openRate: '62%', clickRate: '16%', reportRate: '61%', status: 'Completed', severity: 'Medium' },
    { id: 'CAM-2025-003', name: 'IT Support Spoofing Exercise', template: 'IT Support Request', sendDate: 'Sep 23, 2025', targetGroups: 'IT Department', emailsSent: 89, clicked: 8, reported: 78, openRate: '75%', clickRate: '9%', reportRate: '88%', status: 'Completed', severity: 'Low' },
    { id: 'CAM-2025-004', name: 'HR Benefits Update Scam', template: 'HR Benefits Form', sendDate: 'Sep 22, 2025', targetGroups: 'All Employees', emailsSent: 3200, clicked: 487, reported: 289, openRate: '55%', clickRate: '15%', reportRate: '9%', status: 'Active', severity: 'Critical' },
    { id: 'CAM-2025-005', name: 'Password Reset Phishing', template: 'Password Reset Link', sendDate: 'Sep 20, 2025', targetGroups: 'Random Selection', emailsSent: 1560, clicked: 267, reported: 124, openRate: '52%', clickRate: '17%', reportRate: '8%', status: 'Active', severity: 'Critical' },
  ])

  const [templates, setTemplates] = useState([
    { id: 'TPL-001', name: 'Fake Invoice Alert', category: 'Financial', difficulty: 'Easy', effectiveness: '42%' },
    { id: 'TPL-002', name: 'CEO Impersonation', category: 'Executive', difficulty: 'Medium', effectiveness: '68%' },
    { id: 'TPL-003', name: 'IT Support Request', category: 'Technical', difficulty: 'Hard', effectiveness: '85%' },
    { id: 'TPL-004', name: 'HR Benefits Form', category: 'HR', difficulty: 'Medium', effectiveness: '54%' },
    { id: 'TPL-005', name: 'Password Reset Link', category: 'Security', difficulty: 'Easy', effectiveness: '38%' },
    { id: 'TPL-006', name: 'Package Delivery', category: 'Logistics', difficulty: 'Easy', effectiveness: '51%' },
  ])

  const [filterStatus, setFilterStatus] = useState('all')
  const [filterSeverity, setFilterSeverity] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('campaigns')
  const [showCreateModal, setShowCreateModal] = useState(false)

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      const matchStatus = filterStatus === 'all' || c.status === filterStatus
      const matchSeverity = filterSeverity === 'all' || c.severity === filterSeverity
      const matchSearch = searchQuery === '' ||
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.id.toLowerCase().includes(searchQuery.toLowerCase())
      return matchStatus && matchSeverity && matchSearch
    })
  }, [campaigns, filterStatus, filterSeverity, searchQuery])

  const totalEmailsSent = campaigns.reduce((sum, c) => sum + c.emailsSent, 0)
  const totalClicked = campaigns.reduce((sum, c) => sum + c.clicked, 0)
  const totalReported = campaigns.reduce((sum, c) => sum + c.reported, 0)
  const avgClickRate = (totalClicked / totalEmailsSent * 100).toFixed(1)
  const avgReportRate = (totalReported / totalEmailsSent * 100).toFixed(1)
  const trainingCompliance = '76%'

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'Critical': return 'bg-red-500/20 text-red-300 border-red-500/50'
      case 'High': return 'bg-orange-500/20 text-orange-300 border-orange-500/50'
      case 'Medium': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50'
      default: return 'bg-blue-500/20 text-blue-300 border-blue-500/50'
    }
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return 'bg-red-500/20 text-red-300'
      case 'Completed': return 'bg-green-500/20 text-green-300'
      case 'Paused': return 'bg-yellow-500/20 text-yellow-300'
      default: return 'bg-slate-500/20 text-slate-300'
    }
  }

  const stats = [
    { label: 'Total Emails Sent', value: totalEmailsSent.toLocaleString(), icon: Send, color: 'from-blue-600 to-blue-500', trend: '+45% this month' },
    { label: 'Click Rate', value: avgClickRate + '%', icon: MousePointerClick, color: 'from-orange-600 to-orange-500', trend: 'Industry avg: 14%' },
    { label: 'Report Rate', value: avgReportRate + '%', icon: CheckCircle2, color: 'from-green-600 to-green-500', trend: '+3% vs last month' },
    { label: 'Training Compliance', value: trainingCompliance, icon: Target, color: 'from-purple-600 to-purple-500', trend: 'Target: 85%' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-600 via-red-600 to-orange-700 p-8 text-white shadow-2xl border border-orange-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-red-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Mail className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">PHISHING SIM TOOL</h1>
                </div>
                <p className="text-orange-100 text-lg font-semibold">Employee Security Awareness Training Platform</p>
                <p className="text-orange-200 text-sm mt-1">Automated Phishing Simulation Campaigns for Security Training & Compliance</p>
              </div>
              <div className="text-right">
                <div className="text-5xl font-black text-yellow-300">{campaigns.length}</div>
                <div className="text-sm text-orange-100">Active Campaigns</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-orange-500/20 p-6 hover:border-orange-400/40 transition-all shadow-lg hover:shadow-orange-500/20">
                <div className="flex items-start justify-between mb-4">
                  <div className={`bg-gradient-to-br ${stat.color} p-3 rounded-lg shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs font-bold text-green-400">{stat.trend}</span>
                </div>
                <p className="text-orange-300 text-sm font-semibold">{stat.label}</p>
                <p className="text-white text-3xl font-black mt-2">{stat.value}</p>
              </div>
            )
          })}
        </div>

        {/* Main Content */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-orange-500/20 shadow-lg overflow-hidden">
          {/* Tabs */}
          <div className="flex items-center justify-between border-b border-orange-500/20 p-6">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('campaigns')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 ${
                  activeTab === 'campaigns'
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Send className="w-5 h-5" />
                <span>Campaigns</span>
              </button>
              <button
                onClick={() => setActiveTab('results')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 ${
                  activeTab === 'results'
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span>Results</span>
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 ${
                  activeTab === 'templates'
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Mail className="w-5 h-5" />
                <span>Templates</span>
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center space-x-2 ${
                  activeTab === 'analytics'
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <TrendingUp className="w-5 h-5" />
                <span>Analytics</span>
              </button>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold flex items-center space-x-2 transition-all"
            >
              <Plus className="w-5 h-5" />
              <span>New Campaign</span>
            </button>
          </div>

          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <div className="p-6 space-y-6">
              {/* Filter Bar */}
              <div className="flex space-x-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search campaigns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-orange-500/50"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500/50 font-semibold"
                >
                  <option value="all">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Completed">Completed</option>
                  <option value="Paused">Paused</option>
                </select>
                <select
                  value={filterSeverity}
                  onChange={(e) => setFilterSeverity(e.target.value)}
                  className="bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500/50 font-semibold"
                >
                  <option value="all">All Difficulty</option>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                </select>
              </div>

              {/* Campaigns Table */}
              <div className="bg-slate-700/30 rounded-lg border border-orange-500/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-700/50 border-b border-orange-500/20 sticky top-0">
                      <tr>
                        <th className="px-6 py-4 text-left font-bold text-orange-300">Campaign</th>
                        <th className="px-6 py-4 text-left font-bold text-orange-300">Template</th>
                        <th className="px-6 py-4 text-left font-bold text-orange-300">Sent</th>
                        <th className="px-6 py-4 text-left font-bold text-orange-300">Clicked</th>
                        <th className="px-6 py-4 text-left font-bold text-orange-300">Reported</th>
                        <th className="px-6 py-4 text-left font-bold text-orange-300">Status</th>
                        <th className="px-6 py-4 text-left font-bold text-orange-300">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {filteredCampaigns.map((campaign) => (
                        <tr key={campaign.id} className="hover:bg-slate-700/50 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-white font-semibold">{campaign.name}</p>
                              <p className="text-slate-400 text-xs">{campaign.id}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-300">{campaign.template}</td>
                          <td className="px-6 py-4 text-cyan-400 font-bold">{campaign.emailsSent.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <span className="text-orange-400 font-bold">{campaign.clicked}</span>
                            <span className="text-slate-400 text-xs ml-1">({campaign.clickRate})</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-green-400 font-bold">{campaign.reported}</span>
                            <span className="text-slate-400 text-xs ml-1">({campaign.reportRate})</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-xs font-bold px-3 py-1 rounded-lg border ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 flex space-x-2">
                            <button className="p-2 hover:bg-slate-600 rounded transition-colors text-blue-400" title="View">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-2 hover:bg-slate-600 rounded transition-colors text-yellow-400" title="Edit">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="p-2 hover:bg-slate-600 rounded transition-colors text-red-400" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-700/30 rounded-lg border border-orange-500/20 p-6">
                  <h3 className="text-white font-bold text-lg mb-4 flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-orange-400" />
                    <span>User Behavior Analysis</span>
                  </h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Users who Clicked', value: totalClicked, percentage: avgClickRate, color: 'bg-orange-500' },
                      { label: 'Users who Reported', value: totalReported, percentage: avgReportRate, color: 'bg-green-500' },
                      { label: 'Users Trained', value: Math.round(totalReported * 1.5), percentage: '87%', color: 'bg-blue-500' },
                    ].map((item, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-slate-300">{item.label}</span>
                          <span className="text-white font-bold">{item.value} ({item.percentage})</span>
                        </div>
                        <div className="w-full bg-slate-600 rounded-full h-3">
                          <div className={`${item.color} h-3 rounded-full`} style={{width: item.percentage}}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-700/30 rounded-lg border border-orange-500/20 p-6">
                  <h3 className="text-white font-bold text-lg mb-4 flex items-center space-x-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    <span>High-Risk Users</span>
                  </h3>
                  <div className="space-y-3">
                    {[
                      { name: 'Marketing Dept', clickers: 45, percentage: '18%' },
                      { name: 'Sales Team', clickers: 38, percentage: '15%' },
                      { name: 'New Hires', clickers: 52, percentage: '21%' },
                    ].map((item, idx) => (
                      <div key={idx} className="p-3 bg-slate-600/30 rounded-lg border border-slate-500/20">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-semibold">{item.name}</span>
                          <span className="text-red-400 font-bold">{item.clickers} users ({item.percentage})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <div key={template.id} className="bg-slate-700/30 rounded-lg border border-orange-500/20 p-4 hover:border-orange-400/40 transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-white font-bold">{template.name}</h4>
                        <p className="text-slate-400 text-xs">{template.category}</p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded ${template.difficulty === 'Easy' ? 'bg-green-500/20 text-green-300' : template.difficulty === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-red-500/20 text-red-300'}`}>
                        {template.difficulty}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-600">
                      <span className="text-slate-400 text-xs">Effectiveness</span>
                      <span className="text-orange-400 font-bold">{template.effectiveness}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-700/30 rounded-lg border border-orange-500/20 p-6">
                  <h3 className="text-white font-bold text-lg mb-4">Campaign Performance Trend</h3>
                  <div className="space-y-3">
                    {campaigns.slice(0, 5).map((c, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-slate-300 text-sm">{c.name.substring(0, 25)}</span>
                          <span className="text-orange-400 font-bold text-sm">{c.clickRate}</span>
                        </div>
                        <div className="w-full bg-slate-600 rounded-full h-2">
                          <div className="bg-orange-500 h-2 rounded-full" style={{width: c.clickRate}}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-700/30 rounded-lg border border-orange-500/20 p-6">
                  <h3 className="text-white font-bold text-lg mb-4">Training ROI</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-600/30 rounded-lg border border-slate-500/20">
                      <p className="text-slate-400 text-sm">Avg Click Rate Before Training</p>
                      <p className="text-white text-3xl font-bold">18.5%</p>
                    </div>
                    <div className="p-4 bg-slate-600/30 rounded-lg border border-slate-500/20">
                      <p className="text-slate-400 text-sm">Avg Click Rate After Training</p>
                      <p className="text-green-400 text-3xl font-bold">8.2%</p>
                    </div>
                    <div className="p-4 bg-slate-600/30 rounded-lg border border-slate-500/20">
                      <p className="text-slate-400 text-sm">Improvement</p>
                      <p className="text-green-400 text-2xl font-bold">↓ 55.7%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* AI Training Assistant */}
        <div className="bg-gradient-to-br from-purple-900/30 via-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-8 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="bg-gradient-to-br from-purple-600 to-blue-600 p-4 rounded-xl">
              <Target className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-black text-white mb-2">🎯 AI Security Training Assistant</h3>
              <p className="text-slate-300 mb-4">
                Automated phishing simulation platform powered by AI. Automatically targets high-risk user groups, personalizes training content, tracks security behavior improvements, and generates compliance reports. Integrates with HRIS for employee management and learning management systems for training coordination.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-3 border border-purple-500/20">
                  <p className="text-xs text-slate-400 font-bold">Campaigns Deployed</p>
                  <p className="text-2xl font-black text-purple-400">{campaigns.length}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 border border-purple-500/20">
                  <p className="text-xs text-slate-400 font-bold">Employees Trained</p>
                  <p className="text-2xl font-black text-purple-400">2.8K+</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 border border-purple-500/20">
                  <p className="text-xs text-slate-400 font-bold">Awareness Score</p>
                  <p className="text-2xl font-black text-green-400">{trainingCompliance}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 border border-purple-500/20">
                  <p className="text-xs text-slate-400 font-bold">Risk Reduction</p>
                  <p className="text-2xl font-black text-green-400">55.7%</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
